'use client';

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faGlobe, faChartLine } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import useAnalytics from 'src/hooks/analytics/useAnalytics';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { TableRowActions } from '../';
import {
  Tooltip,
  PulseHeader,
  PulseCell,
  PulseColumnMenu,
  PulseColumnDef,
  PulseLoading,
} from 'src/components';
import { AnalyticsErrorOverlay } from '../AnalyticsErrorOverlay';
import { AnalyticsTableHeader } from '../AnalyticsTableHeader';
import { SortSection } from '../AnalyticsTrendsTable/SortSection';
import { ANALYTICS_LOCATION } from 'src/hooks/filters/constants';
import {
  useGetCodeSetBreakdownAnalyticsQuery,
  useGetCodeByNameQuery,
} from 'src/api/redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { toApiPeriod } from 'src/utils/quarterUtils';
import { getNewExport } from 'src/api';
import { AnalyticsResultItem, AnalyticsSortConfig } from '../types';
import { ANALYTICS_DEFAULT_DATE_RANGE } from 'src/components/constants';
import { getCodeSetCodeColumns } from '../../utils';

// Per-code volume plus the code set's distinct total, keyed by volume type
// (`claims`, `claims_total`, `patients`, ...).
type VolumeByCodeResultItem = AnalyticsResultItem<{
  claims?: Record<string, number>;
  claims_total?: number;
  patients?: Record<string, number>;
  patients_total?: number;
  providers?: Record<string, number>;
  providers_total?: number;
  sites?: Record<string, number>;
  sites_total?: number;
}>;

/** Geography name ascending — the fallback whenever a sort key stops applying. */
const DEFAULT_SORT: AnalyticsSortConfig = { sort: 'name', order: 'asc' };

const AnalyticsVolumeByCodeTable = () => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { closeAllButCurrent } = useDrawer();

  const {
    selectedGeographyObjects = [],
    selectedCodeSet = [],
    formattedFiltersForAPI,
    date_range = ANALYTICS_DEFAULT_DATE_RANGE,
    granularity = 'quarter',
    periods = [],
    volumeTypes = [],
    modeType,
  } = useAnalytics(ANALYTICS_LOCATION.VOLUME_BY_CODE) || {};

  // Under the `quarterly-ta-data` flag, send the granularity + periods contract.
  // `granularity` lives INSIDE `date_range` (it's a field of DateRangeSerializer)
  // — a top-level granularity is ignored by the backend and silently collapses
  // results to year buckets. Otherwise keep the legacy gte/lte year range.
  const { quarterlyTaData } = useFlags();
  const timeFilter = useMemo(() => {
    if (quarterlyTaData) {
      return {
        date_range: {
          granularity,
          periods: periods.map(toApiPeriod),
        },
      };
    }
    return {
      date_range: {
        gte: parseInt(date_range.gte),
        lte: parseInt(date_range.lte),
      },
    };
  }, [quarterlyTaData, granularity, periods, date_range]);

  const volumeType = volumeTypes?.[0] || 'claims';
  const totalSortKey = `${volumeType}_total`;

  const [sort, setSort] = useState<AnalyticsSortConfig>(DEFAULT_SORT);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const hasGeographies =
    Array.isArray(selectedGeographyObjects) &&
    selectedGeographyObjects.length > 0;
  const hasCodeSets = selectedCodeSet?.length > 0;

  const shouldFetch = hasGeographies && hasCodeSets;

  // Only one code set is supported by this insight, so the config map has at
  // most one entry — keyed by code set id like the other single-select tables.
  const apiVolumeConfigs = useMemo(() => {
    if (!selectedCodeSet || selectedCodeSet.length === 0) return {};

    return selectedCodeSet.reduce((acc, codeSet) => {
      acc[codeSet.id.toString()] = {
        code_set: codeSet.id,
        mode: modeType || 'treating',
      };
      return acc;
    }, {} as Record<string, any>);
  }, [selectedCodeSet, modeType]);

  const requestData = useMemo(
    () => ({
      volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
      ...timeFilter,
      volume_configs: apiVolumeConfigs,
      geographies: selectedGeographyObjects.map((g) => ({
        id: g.id,
        type: g.type,
      })),
      ...(formattedFiltersForAPI.length > 0 && {
        filters: formattedFiltersForAPI,
      }),
    }),
    [
      volumeTypes,
      timeFilter,
      apiVolumeConfigs,
      selectedGeographyObjects,
      formattedFiltersForAPI,
    ]
  );

  // One column per code in the code set; the code name is both header and key.
  const dataColumns = useMemo(
    () => getCodeSetCodeColumns(selectedCodeSet),
    [selectedCodeSet]
  );

  // Sort keys name both the volume type and the code (`claims.CPT-55700`), so a
  // key left over from another volume type or code set is rejected by the
  // backend. Resolve it synchronously, before the query runs: correcting it in
  // an effect would still fire one request with the stale key first.
  const effectiveSort = useMemo(() => {
    if (sort.sort === 'name') return sort;

    const validSortKeys = new Set([
      totalSortKey,
      ...dataColumns.map((col) => `${volumeType}.${col}`),
    ]);

    return validSortKeys.has(sort.sort) ? sort : DEFAULT_SORT;
  }, [sort, dataColumns, volumeType, totalSortKey]);

  // Converge the stored sort on what is actually being applied, so the column
  // menus stay in step and a key can't come back to life by switching the
  // volume type back. `DEFAULT_SORT` is a stable reference, so this settles
  // after one pass.
  useEffect(() => {
    if (sort !== effectiveSort) {
      setSort(effectiveSort);
    }
  }, [sort, effectiveSort]);

  const {
    currentData: volumeByCodeData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetCodeSetBreakdownAnalyticsQuery(
    {
      params: {
        sort: effectiveSort.sort,
        order: effectiveSort.order,
      },
      data: requestData,
    },
    {
      skip: !shouldFetch,
    }
  );

  const columns: PulseColumnDef[] = useMemo(() => {
    return [
      ...dataColumns.map((col) => ({
        field: col,
        headerName: col,
        width: 200,
        minWidth: 150,
        maxWidth: 400,
        resizable: true,
        sortable: true,
      })),
      ...(hasCodeSets
        ? [
            {
              field: 'total',
              headerName: 'Total',
              width: 200,
              minWidth: 150,
              maxWidth: 400,
              resizable: true,
              sortable: true,
            },
          ]
        : []),
    ];
  }, [dataColumns, hasCodeSets]);

  const { startResizing, isResizing, getGridTemplateColumns } =
    usePulseTableColumns({ columns });

  const { currentData: codeData } = useGetCodeByNameQuery(
    { codeIds: dataColumns },
    {
      skip: dataColumns.length === 0,
    }
  );

  // Code name -> description, shown as a tooltip on each column header.
  const codeDescriptions = useMemo(() => {
    if (!codeData?.results) return {};

    return codeData.results.reduce((acc, code) => {
      acc[code.code_name] = code.description;
      return acc;
    }, {} as Record<string, string>);
  }, [codeData]);

  const displayedGeographies = useMemo((): VolumeByCodeResultItem[] => {
    const geographies = Array.isArray(selectedGeographyObjects)
      ? selectedGeographyObjects
      : [];

    if (volumeByCodeData?.results && volumeByCodeData.results.length > 0) {
      const selectedIds = new Set<string | number>(
        geographies.map((g) => g.id)
      );

      const sortedResults = volumeByCodeData.results.filter(
        (result: VolumeByCodeResultItem) => selectedIds.has(result.id)
      );

      const resultsMap = new Map<string | number, VolumeByCodeResultItem>(
        volumeByCodeData.results.map((result: VolumeByCodeResultItem) => [
          result.id,
          result,
        ])
      );

      const missingGeographies = geographies
        .filter((geography) => !resultsMap.has(geography.id))
        .map(
          (geography): VolumeByCodeResultItem => ({
            id: geography.id,
            name: geography.name || '',
            type: geography.type,
          })
        );

      return [...sortedResults, ...missingGeographies];
    }

    return geographies.map(
      (geography): VolumeByCodeResultItem => ({
        id: geography.id,
        name: geography.name || '',
        type: geography.type,
      })
    );
  }, [volumeByCodeData, selectedGeographyObjects]);

  const handleExport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      try {
        const queryParams = new URLSearchParams({
          sort: effectiveSort.sort,
          order: effectiveSort.order,
        });

        await getNewExport({
          url: `https://api.medscout.io/api/v1/analytics/territory-insights/code-set-breakdown/?${queryParams.toString()}`,
          method: 'POST',
          fileFormat: format,
          columns: ['id', 'name', 'type', volumeType, totalSortKey],
          fileName: `analytics-volume-by-code-${
            new Date().toISOString().split('T')[0]
          }`,
          data: requestData,
        });

        setNotification({
          title: 'Success',
          message:
            'Export initiated - You will receive an email when it is ready for download',
          type: 'success',
        });

        log.event('Export', {
          type: ANALYTICS_LOCATION.VOLUME_BY_CODE,
          source: 'Analytics Volume By Code Table',
          columns: columns.map((col) => col.field),
          count: displayedGeographies.length,
          format,
        });
      } catch (err) {
        log.exception(`Error exporting volume by code: ${err}`);

        setNotification({
          title: 'Error',
          message: err?.error_message || 'Error exporting, please try again.',
          type: 'error',
        });
      }
    },
    [
      columns,
      volumeType,
      totalSortKey,
      requestData,
      effectiveSort,
      log,
      setNotification,
      displayedGeographies,
    ]
  );

  const handleOpenTerritoryDrawer = useCallback(() => {
    log.event('Territory drawer opened', {
      source: 'AnalyticsVolumeByCodeTable',
    });
    closeAllButCurrent('analytics-territory-drawer');
  }, [closeAllButCurrent, log]);

  const handleEllipsisClick = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.stopPropagation();
      const isOpening = openMenuId !== columnId;
      if (isOpening) {
        log.event('Column menu opened', {
          source: 'AnalyticsVolumeByCodeTable',
          columnId,
        });
      }
      setOpenMenuId((prev) => (prev === columnId ? null : columnId));
    },
    [openMenuId, log]
  );

  const handleSortToggle = useCallback(
    (columnName: string, order?: 'asc' | 'desc') => {
      const newOrder =
        order ||
        (effectiveSort.sort === columnName
          ? effectiveSort.order === 'asc'
            ? 'desc'
            : 'asc'
          : 'desc');

      log.event('Sort changed', {
        source: 'AnalyticsVolumeByCodeTable',
        columnName,
        sortOrder: newOrder,
      });

      setSort({ sort: columnName, order: newOrder });
      setOpenMenuId(null);
    },
    [effectiveSort, log]
  );

  const hasUsableCodes = dataColumns.length > 0;
  const showGeographyEmptyState = !hasGeographies;
  const showCodeSetEmptyState = !hasCodeSets;
  const showDrugOnlyWarning = hasGeographies && hasCodeSets && !hasUsableCodes;

  const showLoadingOverlay =
    (isLoading || isFetching) && hasGeographies && hasCodeSets;

  const totalDataColumns = hasCodeSets
    ? dataColumns.length + 1
    : dataColumns.length;

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden bg-white relative rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),0px_1px_2px_-1px_rgba(0,0,0,0.00)] border border-neutral-200"
      data-testid="analytics-volume-by-code-table"
    >
      <AnalyticsTableHeader
        columns={dataColumns}
        location={ANALYTICS_LOCATION.VOLUME_BY_CODE}
        onExport={handleExport}
      />

      <div
        className={`flex-1 relative ${
          hasGeographies
            ? 'overflow-x-auto overflow-y-auto'
            : 'overflow-hidden'
        }`}
        data-testid="volume-by-code-table-container"
      >
        {/* Full-height separator line between Geography and data columns */}
        {(hasGeographies || showGeographyEmptyState) && (
          <div className="sticky left-[271px] top-12 h-[calc(100%-3rem)] w-px bg-neutral-200 z-[21] pointer-events-none float-left -ml-px" />
        )}

        <div
          role="table"
          aria-label="Analytics volume by code data by geography"
          className="grid relative z-10 w-full"
          data-testid="volume-by-code-table"
          style={{
            gridTemplateColumns:
              totalDataColumns === 0
                ? '272px 1fr'
                : `272px ${getGridTemplateColumns()}`,
            gridAutoFlow: 'row',
          }}
        >
          <PulseHeader
            label="Geography"
            isSticky
            stickyPosition="both"
            align="left"
            className="z-30 bg-neutral-50 text-neutral-900 border-b border-r border-b-neutral-200 border-r-neutral-200"
            sortable
            sortDirection={effectiveSort.sort === 'name' ? effectiveSort.order : null}
            resizable={false}
            menu={
              <PulseColumnMenu
                columnId="name"
                isOpen={openMenuId === 'name'}
                onToggle={(e) => handleEllipsisClick(e, 'name')}
                customSectionComponents={[
                  <SortSection
                    key="sort"
                    currentOrder={effectiveSort.order}
                    isActive={effectiveSort.sort === 'name'}
                    onChange={(order) => handleSortToggle('name', order)}
                  />,
                ]}
              />
            }
          />

          {dataColumns.map((col) => {
            const sortValue = `${volumeType}.${col}`;
            const isMenuOpen = openMenuId === sortValue;
            const isActiveSortColumn = effectiveSort.sort === sortValue;
            const description = codeDescriptions[col];

            return (
              <PulseHeader
                key={col}
                label={
                  <div className="flex items-center gap-2">
                    <span className="truncate">{col}</span>
                    {description && (
                      <Tooltip
                        content={description}
                        position="top"
                        slotProps={{
                          contentClassName: 'max-w-xs',
                        }}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <Icon
                            icon={faInfoCircle}
                            className="w-4 h-4 text-secondary"
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                }
                isSticky
                stickyPosition="top"
                align="left"
                className="z-20 bg-neutral-50 text-neutral-800 border-b border-r border-b-neutral-200 border-r-neutral-200"
                sortable
                sortDirection={isActiveSortColumn ? effectiveSort.order : null}
                resizable={true}
                onStartResize={(e) => startResizing(col, e)}
                isResizing={isResizing}
                menu={
                  <PulseColumnMenu
                    columnId={sortValue}
                    isOpen={isMenuOpen}
                    onToggle={(e) => handleEllipsisClick(e, sortValue)}
                    customSectionComponents={[
                      <SortSection
                        key="sort"
                        currentOrder={effectiveSort.order}
                        isActive={isActiveSortColumn}
                        onChange={(order) => handleSortToggle(sortValue, order)}
                      />,
                    ]}
                  />
                }
              />
            );
          })}

          {hasCodeSets && (
            <PulseHeader
              label={
                <div className="flex items-center gap-2">
                  <span>Total</span>
                  <Tooltip
                    content="Counted across the whole code set, so it is not the sum of the code columns — the same patient, provider or site can appear under more than one code."
                    position="top"
                    slotProps={{
                      contentClassName: 'max-w-xs',
                    }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <Icon
                        icon={faInfoCircle}
                        className="w-4 h-4 text-secondary"
                      />
                    </div>
                  </Tooltip>
                </div>
              }
              isSticky
              stickyPosition="top"
              align="left"
              className="z-20 bg-neutral-50 text-neutral-800 border-b border-r border-b-neutral-200 border-r-neutral-200"
              sortable
              sortDirection={
                effectiveSort.sort === totalSortKey ? effectiveSort.order : null
              }
              resizable={true}
              onStartResize={(e) => startResizing('total', e)}
              isResizing={isResizing}
              menu={
                <PulseColumnMenu
                  columnId={totalSortKey}
                  isOpen={openMenuId === totalSortKey}
                  onToggle={(e) => handleEllipsisClick(e, totalSortKey)}
                  customSectionComponents={[
                    <SortSection
                      key="sort"
                      currentOrder={effectiveSort.order}
                      isActive={effectiveSort.sort === totalSortKey}
                      onChange={(order) => handleSortToggle(totalSortKey, order)}
                    />,
                  ]}
                />
              }
            />
          )}

          {/* Filler Column Header - empty cell to fill remaining space */}
          <div className="bg-neutral-50 border-b border-b-neutral-200 sticky top-0 z-20" />

          {/* Body of the table */}
          {showGeographyEmptyState && (
            <div className="flex sticky left-0 items-center justify-center w-[272px] h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5">
              <div className="flex flex-col items-center justify-center gap-2">
                <Icon icon={faGlobe} className="text-2xl" />
                <span>Select Geographies</span>
                <button
                  type="button"
                  onClick={handleOpenTerritoryDrawer}
                  className="px-4 py-2 bg-white text-secondary border border-neutral-200 text-sm font-medium font-['Inter'] rounded-full hover:bg-neutral-100 transition-all duration-300"
                >
                  Add Geographies
                </button>
              </div>
            </div>
          )}
          {/* Code set empty state - State 1: Part of grid when no geographies */}
          {showGeographyEmptyState && showCodeSetEmptyState && (
            <div
              className="flex items-center justify-center bg-white h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5 col-start-2 col-end-[-1]"
              data-testid="volume-by-code-empty-grid-cell"
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <Icon icon={faChartLine} className="text-2xl" />
                <span>Select a Code Set to see volume by code</span>
              </div>
            </div>
          )}

          {hasGeographies &&
            displayedGeographies.map((geography) => {
              const isRegion = geography.type === 'region';
              const volumeByCode = geography?.[volumeType] as
                | Record<string, number>
                | undefined;
              const total = geography?.[totalSortKey];

              return (
                <React.Fragment key={geography.id}>
                  <PulseCell
                    isSticky
                    stickyPosition="left"
                    className="z-30 border-b border-b-neutral-200 bg-white group"
                  >
                    <div className="flex items-center justify-between w-full gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          data-testid={`geography-name-${geography.id}`}
                          className="truncate text-secondary text-sm font-normal font-['Inter'] leading-5"
                          title={geography.name}
                        >
                          {geography.name}
                        </span>
                        {isRegion && (
                          <Tooltip
                            content="This is a region"
                            slotProps={{
                              containerClassName: '!bg-neutral-900 !text-white',
                            }}
                          >
                            <span
                              className="inline-flex items-center text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-full cursor-default shrink-0"
                              data-testid={`region-badge-${geography.id}`}
                              role="status"
                              aria-label="Region indicator"
                            >
                              Region
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <TableRowActions
                        geographyId={geography.id}
                        location={ANALYTICS_LOCATION.VOLUME_BY_CODE}
                        isRegion={isRegion}
                      />
                    </div>
                  </PulseCell>

                  {dataColumns.map((col) => {
                    const value = volumeByCode?.[col];

                    return (
                      <PulseCell
                        key={col}
                        align="left"
                        className={`bg-white ${
                          hasCodeSets
                            ? 'border-b border-r border-b-neutral-200 border-r-neutral-200'
                            : ''
                        }`}
                      >
                        {/* PulseCell doesn't forward data-* props, so the test
                            hook lives on the value itself. */}
                        <span
                          data-testid={`volume-by-code-cell-${geography.id}-${col}`}
                        >
                          {hasCodeSets
                            ? value != null
                              ? handleNumericValue(value)
                              : 'n/a'
                            : null}
                        </span>
                      </PulseCell>
                    );
                  })}

                  {hasCodeSets && (
                    <PulseCell
                      align="left"
                      className="bg-white border-b border-r border-b-neutral-200 border-r-neutral-200"
                    >
                      <span data-testid={`volume-by-code-total-${geography.id}`}>
                        {total != null
                          ? handleNumericValue(total as number)
                          : 'n/a'}
                      </span>
                    </PulseCell>
                  )}

                  {/* Filler Column Cell - empty cell to fill remaining space */}
                  <div
                    className={`bg-white ${
                      hasCodeSets ? 'border-b border-b-neutral-200' : ''
                    }`}
                  />
                </React.Fragment>
              );
            })}
        </div>

        {/* Code Set Empty State Overlay - State 2: Shows when we have geographies but no code set */}
        {!showGeographyEmptyState && showCodeSetEmptyState && (
          <div
            className="absolute left-72 top-12 right-0 bottom-0 z-30 bg-white flex items-center justify-center text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
            data-testid="code-set-empty-state-overlay"
          >
            <div className="flex flex-col items-center justify-center gap-2">
              <Icon icon={faChartLine} className="text-2xl" />
              <span>Select a Code Set to see volume by code</span>
            </div>
          </div>
        )}

        {/* Drug Only Warning Overlay - every code in the set was a drug code */}
        {showDrugOnlyWarning && (
          <div
            className="absolute left-72 top-12 right-0 bottom-0 z-30 bg-white flex items-center justify-center text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
            data-testid="volume-by-code-drug-only-warning-overlay"
          >
            <div className="flex flex-col items-center gap-2 max-w-md text-center">
              <Icon icon={faChartLine} className="text-2xl" />
              <p className="text-sm text-neutral-700">
                There are no procedure or diagnosis codes in this code set.
                Please select a code set with procedure or diagnosis codes.
              </p>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        <AnalyticsErrorOverlay
          error={error}
          isVisible={isError && !isLoading && !isFetching}
        />

        {showLoadingOverlay && <PulseLoading />}
      </div>
    </div>
  );
};

export default AnalyticsVolumeByCodeTable;
